import { useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import {
  askCaseQuestionStream,
  askDashboardQuestionStream,
  askDraftingAssistantStream,
  lastAiSources,
  type Source,
} from '../lib/ai';
import { ApiError, ApiNotConfiguredError } from '../lib/apiClient';
import {
  parseSlashCommand,
  type LexCounselSlashCommand,
} from '../lib/lexCounselCommands';
import { answerDashboardQuestion, isDashboardBriefingQuestion } from '../lib/lexCounselDashboardBrief';
import {
  createConversation,
  loadConversations,
  loadLegacyAiChats,
  updateConversation,
} from '../lib/lexCounselConversations';
import {
  buildCaseWorkspaceContext,
  buildDashboardWorkspaceContext,
  buildDraftEditorWorkspaceContext,
  buildLibraryWorkspaceContext,
} from '../lib/workspaceContext';
import {
  buildUnifiedDraftingRequest,
  hasValidWorkspaceSelection,
  inferDraftScope,
  snapshotWorkspaceForDrafting,
} from '../lib/draftingRequestContext';
import { formatCounselPanelHtml } from '../lib/formatCounselPanelHtml';
import {
  consumeUserSwitchedBackTelemetry,
  recordAutoSwitchForTelemetry,
  resolveEditorCounselMode,
  type EditorCounselMode,
} from '../lib/lexCounselEditorMode';
import { trackEvent } from '../lib/analytics';
import {
  counselNodeStatusLabel,
  counselStreamIntoDocumentPanelNote,
  hasRenderableCounselStreamContent,
  type CounselActivityMode,
} from '../lib/counselStreamVisibility';
import {
  createDocumentStreamDeltaScheduler,
  dispatchDocumentStreamFinal,
  dispatchDocumentStreamStart,
  dispatchDocumentStreamCancel,
} from '../lib/editor/documentStreamEvents';
import { isDocumentBlank } from '../lib/draftingWorkflow/insertion';
import type { LexCounselSendScope } from '../lib/lexCounselSend';
import {
  createCounselSendGuard,
  registerCounselSendCancel,
  type CounselSendGuard,
} from '../lib/lexCounselSendGuard';
import { normalizeCounselDraftHtml } from '../lib/editor/normalizeCounselDraftHtml';
import { normalizeSelectionReplacementHtml } from '../lib/draftParagraphUtils';
import {
  buildDraftChangeReason,
  buildDraftKeyPoints,
  isHighImpactIdentifierEdit,
} from '../lib/draftChangeSummary';
import type {
  DraftChangeScope,
  LexCounselMessage,
  LexCounselWorkspaceSnapshot,
  PendingDraftChange,
} from '../store/lexCounselStore';
import { useLexCounselStore } from '../store/lexCounselStore';

function counselActivityMode(
  editorCounselMode?: EditorCounselMode,
  screen?: LexCounselWorkspaceSnapshot['screen'],
): CounselActivityMode {
  if (screen === 'case') return 'case';
  if (screen === 'dashboard') return 'workspace';
  if (editorCounselMode === 'review') return 'review';
  if (editorCounselMode === 'draft') return 'draft';
  return 'ask';
}

function counselTokenStreamPatch(params: {
  accumulated: string;
  activityMode: CounselActivityMode;
  streamIntoDocument: boolean;
  fallbackStatus?: string;
}): Partial<LexCounselMessage> {
  const { accumulated, activityMode, streamIntoDocument, fallbackStatus } = params;
  if (streamIntoDocument) {
    return {
      text: '',
      streaming: true,
      thinkingStatus: counselNodeStatusLabel('writing_document', activityMode),
      activityNote: counselStreamIntoDocumentPanelNote(),
    };
  }
  const renderable = hasRenderableCounselStreamContent(accumulated);
  const statusWhileStreaming =
    fallbackStatus ??
    (renderable
      ? counselNodeStatusLabel('answering', activityMode)
      : counselNodeStatusLabel('drafting', activityMode));
  return {
    text: accumulated,
    streaming: true,
    thinkingStatus: statusWhileStreaming,
    activityNote: undefined,
  };
}

type MessagesUpdater = LexCounselMessage[] | ((prev: LexCounselMessage[]) => LexCounselMessage[]);

function createStreamingUpdater(
  onMessages: (updater: MessagesUpdater) => void,
  shouldApply?: () => boolean,
) {
  let rafId: number | null = null;
  let pending: ((prev: LexCounselMessage[]) => LexCounselMessage[]) | null = null;

  const flush = () => {
    rafId = null;
    if (!pending) return;
    if (shouldApply && !shouldApply()) {
      pending = null;
      return;
    }
    const fn = pending;
    pending = null;
    flushSync(() => {
      onMessages(fn);
    });
  };

  return {
    schedule(updater: (prev: LexCounselMessage[]) => LexCounselMessage[]) {
      pending = updater;
      if (rafId === null) {
        rafId = requestAnimationFrame(flush);
      }
    },
    immediate(updater: (prev: LexCounselMessage[]) => LexCounselMessage[]) {
      if (shouldApply && !shouldApply()) return;
      flushSync(() => {
        onMessages(updater);
      });
    },
    cancel() {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      pending = null;
    },
  };
}

function patchStreamingAiMessage(
  prev: LexCounselMessage[],
  patch: Partial<LexCounselMessage>,
): LexCounselMessage[] {
  const next = [...prev];
  const last = next[next.length - 1];
  if (last?.role === 'ai') {
    next[next.length - 1] = { ...last, ...patch };
    return next;
  }
  // History reload can drop the streaming placeholder; recreate it so the
  // loading badge and later token patches keep working.
  next.push({
    role: 'ai',
    text: '',
    streaming: true,
    sources: [],
    thinkingStatus: counselNodeStatusLabel('gathering_context', 'ask'),
    ...patch,
  });
  return next;
}

async function revealTextGradually(
  fullText: string,
  newHistory: LexCounselMessage[],
  onMessages: (updater: MessagesUpdater) => void,
  persist: (history: LexCounselMessage[]) => Promise<void>,
  extra?: Partial<LexCounselMessage>,
): Promise<void> {
  const plain = fullText.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const step = Math.max(2, Math.ceil(plain.length / 80));
  let shown = 0;

  while (shown < plain.length) {
    shown = Math.min(plain.length, shown + step);
    const slice = plain.slice(0, shown);
    flushSync(() => {
      onMessages([
        ...newHistory,
        { role: 'ai', text: slice, streaming: true, thinkingStatus: undefined, ...extra },
      ]);
    });
    await new Promise((resolve) => setTimeout(resolve, 16));
  }

  const finalHistory = [
    ...newHistory,
    { role: 'ai', text: fullText, streaming: false, thinkingStatus: undefined, ...extra },
  ];
  onMessages(finalHistory);
  await persist(finalHistory);
}

function formatStructuredHtml(title: string, sections: Array<{ heading: string; body: string }>): string {
  const parts = sections
    .filter((s) => s.body.trim())
    .map((s) => `<h4>${s.heading}</h4><p>${s.body.replace(/\n/g, '<br/>')}</p>`);
  return `<p><strong>${title}</strong></p>${parts.join('')}`;
}

function appendStreamingAuthority(prev: LexCounselMessage[], source: Source): LexCounselMessage[] {
  const next = [...prev];
  const last = next[next.length - 1];
  if (last?.role !== 'ai') return next;
  const existing = last.sources ?? [];
  const key = `${source.label}::${source.statuteId}::${source.sectionId ?? ''}`;
  if (existing.some((s) => `${s.label}::${s.statuteId}::${s.sectionId ?? ''}` === key)) {
    return next;
  }
  next[next.length - 1] = { ...last, sources: [...existing, source] };
  return next;
}

function formatLexCounselError(err: unknown): string {
  if (err instanceof ApiNotConfiguredError) {
    return '<p>The AI backend is not configured. Leave <code>VITE_API_BASE_URL</code> empty for local dev (Vite proxies <code>/api</code>) or set it to your backend URL, then restart <code>npm run dev</code>.</p>';
  }
  if (err instanceof ApiError) {
    return `<p>${err.message}</p>`;
  }
  if (err instanceof Error && err.message) {
    return `<p>${err.message}</p>`;
  }
  return '<p>Sorry, something went wrong. Please try again.</p>';
}

function stripHtmlToParagraph(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return '<p></p>';
  if (trimmed.startsWith('<')) return trimmed;
  return `<p>${trimmed}</p>`;
}

function isSemanticallyEmptyParagraphHtml(html: string): boolean {
  const text = html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return !text;
}

function resolveDraftApplyAnswer(answer: string, streamFallback: string): string {
  const trimmed = answer.trim();
  if (trimmed) return normalizeCounselDraftHtml(trimmed);
  const acc = streamFallback.trim();
  if (!acc) return answer;
  if (/<[a-z][\s\S]*>/i.test(acc)) return normalizeCounselDraftHtml(acc);
  const paragraphs = acc.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  if (paragraphs.length === 0) return normalizeCounselDraftHtml(stripHtmlToParagraph(acc));
  return normalizeCounselDraftHtml(
    paragraphs.map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`).join(''),
  );
}

function formatCounselStreamAnswer(answer: string, streamFallback: string): string {
  const trimmed = answer.trim();
  if (trimmed) return formatCounselPanelHtml(answer);
  const acc = streamFallback.trim();
  if (!acc) return '';
  return formatCounselPanelHtml(acc);
}

function buildDraftChangeFromAnswer(params: {
  scope: DraftChangeScope;
  answer: string;
  workspace: LexCounselWorkspaceSnapshot;
  targetParagraphId?: string;
  targetParagraphHtml?: string;
}): PendingDraftChange {
  const { scope, answer, workspace, targetParagraphId, targetParagraphHtml } = params;
  const id = crypto.randomUUID();

  const tabId = workspace.activeTabId;

  if (scope === 'document') {
    return {
      id,
      scope: 'document',
      tabId,
      originalHtml: workspace.draftText?.trim() || '<p></p>',
      proposedHtml: answer,
    };
  }

  if (scope === 'insert') {
    return {
      id,
      scope: 'insert',
      tabId,
      originalHtml: '',
      proposedHtml: answer,
    };
  }

  if (scope === 'new_tab') {
    return {
      id,
      scope: 'new_tab',
      originalHtml: '',
      proposedHtml: answer,
    };
  }

  if (scope === 'selection') {
    return {
      id,
      scope: 'selection',
      tabId,
      paragraphId: targetParagraphId,
      selectionFrom: workspace.selectionFrom,
      selectionTo: workspace.selectionTo,
      originalHtml:
        workspace.selectedHtml?.trim() ||
        (workspace.selectedText?.trim()
          ? `<span>${workspace.selectedText.trim()}</span>`
          : ''),
      proposedHtml: normalizeSelectionReplacementHtml(answer),
    };
  }

  return {
    id,
    scope,
    tabId,
    paragraphId: targetParagraphId,
    originalHtml: targetParagraphHtml ?? '<p></p>',
    proposedHtml: stripHtmlToParagraph(answer),
  };
}

function draftChangeMetadata(
  prompt: string,
  proposedHtml: string,
): Pick<PendingDraftChange, 'reason' | 'risk' | 'status' | 'keyPoints'> {
  const highImpact = isHighImpactIdentifierEdit(prompt);
  return {
    reason: buildDraftChangeReason(prompt, highImpact),
    keyPoints: buildDraftKeyPoints(proposedHtml, prompt),
    risk: highImpact ? 'high' : 'standard',
    status: 'pending',
  };
}

function needsLawyerNameClarification(prompt: string): boolean {
  const normalized = prompt.trim().toLowerCase();
  const requestsLawyerNameChange =
    /\b(correct|fix|replace|update|change)\b/.test(normalized) &&
    /\b(lawyer|advocate|counsel)\s+name\b/.test(normalized);
  if (!requestsLawyerNameChange) return false;

  // Require a deliberate replacement value, e.g. “replace X with Y” or
  // “change the lawyer name to Y”; never guess a legal identity.
  return !/\b(?:to|with)\s+['"]?[a-z][\p{L}.'\-\s]{2,}/iu.test(prompt);
}

function finalizeDraftStreamResult(params: {
  answer: string;
  sources: Source[];
  workspace: LexCounselWorkspaceSnapshot;
  newHistory: LexCounselMessage[];
  applyDraftChange: boolean;
  scope: DraftChangeScope;
  hadSelectionText: boolean;
  targetParagraphId?: string;
  targetParagraphHtml?: string;
  fallbackParagraphId?: string;
  fallbackParagraphHtml?: string;
  streamFallback?: string;
  modeSwitchNotice?: string | null;
  onMessages: (updater: LexCounselMessage[] | ((prev: LexCounselMessage[]) => LexCounselMessage[])) => void;
  persist: (history: LexCounselMessage[]) => Promise<void>;
  isActive?: () => boolean;
  prompt: string;
}): void {
  const {
    answer,
    sources,
    workspace,
    newHistory,
    applyDraftChange,
    scope,
    hadSelectionText,
    targetParagraphId,
    targetParagraphHtml,
    fallbackParagraphId,
    fallbackParagraphHtml,
    streamFallback = '',
    modeSwitchNotice = null,
    onMessages,
    persist,
    isActive,
    prompt,
  } = params;

  if (isActive && !isActive()) return;

  const resolvedAnswer = applyDraftChange
    ? resolveDraftApplyAnswer(answer, streamFallback)
    : formatCounselStreamAnswer(answer, streamFallback);
  const trimmedAnswer = resolvedAnswer.trim();
  let draftChange: PendingDraftChange | undefined;
  let scopeFallbackNotice: string | undefined;

  if (applyDraftChange && scope && trimmedAnswer) {
    if (scope === 'document') {
      draftChange = buildDraftChangeFromAnswer({ scope, answer: resolvedAnswer, workspace });
    } else if (scope === 'new_tab') {
      draftChange = buildDraftChangeFromAnswer({ scope, answer: resolvedAnswer, workspace });
    } else if (scope === 'insert') {
      draftChange = buildDraftChangeFromAnswer({ scope: 'insert', answer: resolvedAnswer, workspace });
    } else if (scope === 'selection' && hasValidWorkspaceSelection(workspace)) {
      draftChange = buildDraftChangeFromAnswer({
        scope: 'selection',
        answer: resolvedAnswer,
        workspace,
        targetParagraphId,
        targetParagraphHtml,
      });
    } else if (scope === 'selection' && hadSelectionText) {
      const paraId = targetParagraphId ?? fallbackParagraphId;
      const paraHtml = targetParagraphHtml ?? fallbackParagraphHtml;
      if (paraId) {
        scopeFallbackNotice = 'Selection was lost — applied to the paragraph instead.';
        draftChange = buildDraftChangeFromAnswer({
          scope: 'paragraph',
          answer: resolvedAnswer,
          workspace,
          targetParagraphId: paraId,
          targetParagraphHtml: paraHtml,
        });
      }
    } else if (targetParagraphId) {
      const proposedHtml = stripHtmlToParagraph(resolvedAnswer);
      if (!isSemanticallyEmptyParagraphHtml(proposedHtml)) {
        draftChange = buildDraftChangeFromAnswer({
          scope: 'paragraph',
          answer: resolvedAnswer,
          workspace,
          targetParagraphId,
          targetParagraphHtml,
        });
      }
    }
  }

  if (draftChange && scopeFallbackNotice) {
    draftChange = { ...draftChange, scopeFallbackNotice };
  }
  if (draftChange) {
    draftChange = {
      ...draftChange,
      ...draftChangeMetadata(prompt, draftChange.proposedHtml),
    };
  }

  if (draftChange) {
    useLexCounselStore.getState().setActivePendingChange(draftChange);
    useLexCounselStore.getState().setActiveDraftChangeSet({
      id: crypto.randomUUID(),
      prompt,
      summary:
        draftChange.scope === 'document'
          ? 'Review the draft in your editor, then accept or discard.'
          : 'Review the proposed change before applying it to your draft.',
      tabId: draftChange.tabId,
      status: 'reviewing',
      changes: [draftChange],
      selectedChangeId: draftChange.id,
    });
    window.dispatchEvent(new CustomEvent('lexcounsel-pending-change', { detail: draftChange }));
    if (draftChange.scope === 'selection') {
      window.dispatchEvent(new CustomEvent('lexcounsel-selection-consumed'));
    }
  }

  let text: string;
  if (draftChange) {
    text =
      draftChange.scope === 'document'
        ? '<p>The draft is in your document — review it there, then accept or discard.</p>'
        : draftChange.scope === 'new_tab'
          ? '<p>Accept below to create a new document tab with this draft.</p>'
        : draftChange.scope === 'insert'
          ? '<p>Generated draft is below. Copy it or insert at your cursor.</p>'
          : draftChange.scope === 'selection'
            ? '<p>Review the inline suggestion in your document, then accept or reject.</p>'
            : scopeFallbackNotice
              ? `<p>${scopeFallbackNotice}</p><p>Review the proposed change below, then apply or discard.</p>`
              : '<p>Review the proposed change below, then apply or discard.</p>';
  } else if (trimmedAnswer) {
    text = resolvedAnswer;
  } else if (applyDraftChange && scope === 'selection') {
    text =
      '<p>The assistant returned no text for your selection. Try selecting the passage again and resend.</p>';
  } else {
    text = '<p>No response was generated. Please try again.</p>';
  }

  const aiMsg: LexCounselMessage = {
    role: 'ai',
    text,
    sources,
    streaming: false,
    ...(modeSwitchNotice ? { modeSwitchNotice } : {}),
    ...(draftChange ? { draftChange } : {}),
  };
  const finalHistory = [...newHistory, aiMsg];
  onMessages(finalHistory);
  void persist(finalHistory);
}

async function streamDraftingResponse(params: {
  workspace: LexCounselWorkspaceSnapshot;
  question: string;
  newHistory: LexCounselMessage[];
  wsCtx: ReturnType<typeof workspaceContextForSnapshot>;
  ac: AbortController;
  guard: CounselSendGuard;
  editorCounselMode: EditorCounselMode;
  modeSwitchNotice?: string | null;
  draftScope?: DraftChangeScope;
  commandId?: string;
  targetParagraphId?: string;
  targetParagraphHtml?: string;
  fallbackParagraphId?: string;
  fallbackParagraphHtml?: string;
  onMessages: (updater: LexCounselMessage[] | ((prev: LexCounselMessage[]) => LexCounselMessage[])) => void;
  persist: (history: LexCounselMessage[]) => Promise<void>;
  onRegisterCancel?: (cancel: () => void) => void;
}): Promise<void> {
  const {
    workspace,
    question,
    newHistory,
    wsCtx,
    ac,
    guard,
    editorCounselMode,
    modeSwitchNotice = null,
    draftScope,
    commandId,
    targetParagraphId,
    targetParagraphHtml,
    fallbackParagraphId,
    fallbackParagraphHtml,
    onMessages,
    persist,
    onRegisterCancel,
  } = params;

  const { isActive } = guard;
  const guardedOnMessages = guard.onMessages;
  const guardedPersist = guard.persist;
  const stream = createStreamingUpdater(guardedOnMessages, isActive);
  const applyDraftChange = editorCounselMode === 'draft';

  const workWorkspace = workspace;
  if (applyDraftChange && needsLawyerNameClarification(question)) {
    const aiMsg: LexCounselMessage = {
      role: 'ai',
      text:
        '<p>I can prepare a private review of every lawyer-name occurrence, but I need the exact replacement first. What name should replace the current lawyer name?</p>',
      streaming: false,
    };
    guardedOnMessages([...newHistory, aiMsg]);
    void guardedPersist([...newHistory, aiMsg]);
    return;
  }
  let scope = inferDraftScope(workspace, draftScope, question);

  if (!isActive()) return;

  const hadSelectionText = Boolean(workWorkspace.selectedText?.trim());
  const applyDraftChangeResolved = editorCounselMode === 'draft';
  const counselAskMode = !applyDraftChangeResolved;
  const activityMode = counselActivityMode(editorCounselMode, workWorkspace.screen);
  // Document proposals are injected into the editor for review; Apply/Discard stay in the panel.
  // Paragraph/selection proposals stay private until accepted (inline widgets / panel).
  const streamIntoDocument = false;

  const { instruction, templateOptions } = await buildUnifiedDraftingRequest({
    workspace: workWorkspace,
    userPrompt: question,
    scope: scope ?? 'paragraph',
    commandId,
    selectedText: workspace.selectedText,
    counselAskMode,
  });
  if (!isActive()) return;

  let accumulated = '';
  let finalHandled = false;
  const documentStreamScheduler = streamIntoDocument
    ? createDocumentStreamDeltaScheduler(workWorkspace.activeTabId)
    : null;

  onRegisterCancel?.(() => {
    stream.cancel();
    documentStreamScheduler?.cancel();
  });

  if (streamIntoDocument) {
    window.dispatchEvent(new CustomEvent('lexcounsel-selection-consumed'));
    stream.immediate((prev) =>
      patchStreamingAiMessage(prev, {
        text: '',
        streaming: true,
        thinkingStatus: counselNodeStatusLabel(
          'writing_document',
          activityMode,
        ),
        activityNote: counselStreamIntoDocumentPanelNote(),
        ...(modeSwitchNotice ? { modeSwitchNotice } : {}),
      }),
    );
    dispatchDocumentStreamStart(workWorkspace.activeTabId);
  } else if (modeSwitchNotice) {
    stream.immediate((prev) =>
      patchStreamingAiMessage(prev, {
        modeSwitchNotice,
      }),
    );
  }

  const completeWithAnswer = (answer: string, sources: Source[]) => {
    if (!isActive()) return;
    finalHandled = true;
    documentStreamScheduler?.cancel();
    stream.cancel();
    finalizeDraftStreamResult({
      answer,
      sources,
      workspace: workWorkspace,
      newHistory,
      applyDraftChange: applyDraftChangeResolved,
      scope,
      hadSelectionText,
      targetParagraphId,
      targetParagraphHtml,
      fallbackParagraphId,
      fallbackParagraphHtml,
      streamFallback: accumulated,
      modeSwitchNotice,
      onMessages: guardedOnMessages,
      persist: guardedPersist,
      isActive,
      prompt: question,
    });
  };

  try {
    for await (const event of askDraftingAssistantStream(
      workWorkspace.draftText ?? '',
      workWorkspace.caseData ?? null,
      workWorkspace.proceedings ?? [],
      instruction,
      ac.signal,
      templateOptions,
      {
        activeTabId: workWorkspace.activeTabId,
        draftTabs: workWorkspace.draftTabs as never,
      },
      counselAskMode,
      wsCtx,
      {
        targetParagraphId:
          counselAskMode || scope === 'document' || scope === 'new_tab' || scope === 'insert' || scope === 'selection'
            ? undefined
            : targetParagraphId,
        targetParagraphHtml:
          counselAskMode || scope === 'document' || scope === 'new_tab' || scope === 'insert' || scope === 'selection'
            ? undefined
            : targetParagraphHtml,
        lexCounselMode: counselAskMode
          ? editorCounselMode === 'review'
            ? 'review'
            : 'ask'
          : undefined,
        documentScope: scope === 'document',
        reviewAllTabs: editorCounselMode === 'review',
      },
    )) {
      if (!isActive()) return;
      if (event.type === 'node' && event.data?.name) {
        stream.immediate((prev) =>
          patchStreamingAiMessage(prev, {
            thinkingStatus: counselNodeStatusLabel(String(event.data.name), activityMode),
            ...(streamIntoDocument
              ? {
                  text: '',
                  activityNote: counselStreamIntoDocumentPanelNote(),
                }
              : {}),
          }),
        );
      }
      if (event.type === 'authority' && event.data?.source) {
        stream.schedule((prev) => appendStreamingAuthority(prev, event.data.source as Source));
      }
      if (event.type === 'token' && event.data?.text) {
        accumulated += event.data.text;
        if (streamIntoDocument && documentStreamScheduler) {
          documentStreamScheduler.schedule(accumulated);
          stream.schedule((prev) =>
            patchStreamingAiMessage(
              prev,
              counselTokenStreamPatch({
                accumulated,
                activityMode,
                streamIntoDocument: true,
              }),
            ),
          );
        } else if (!applyDraftChangeResolved) {
          stream.schedule((prev) => {
            const last = prev[prev.length - 1];
            return patchStreamingAiMessage(
              prev,
              counselTokenStreamPatch({
                accumulated,
                activityMode,
                streamIntoDocument: false,
                fallbackStatus: last?.thinkingStatus,
              }),
            );
          });
        } else {
          stream.schedule((prev) => {
            const last = prev[prev.length - 1];
            return patchStreamingAiMessage(prev, {
              streaming: true,
              thinkingStatus:
                last?.thinkingStatus ?? counselNodeStatusLabel('drafting', activityMode),
            });
          });
        }
      }
      if (event.type === 'final' && event.data?.html != null) {
        if (!isActive()) return;
        if (streamIntoDocument) {
          documentStreamScheduler?.cancel();
          const finalHtml = (event.data.html as string)?.trim() || resolveDraftApplyAnswer('', accumulated);
          dispatchDocumentStreamFinal(workWorkspace.activeTabId, finalHtml);
        }
        completeWithAnswer(
          event.data.html as string,
          (event.data.sources as Source[]) ?? lastAiSources.drafting,
        );
      }
    }
  } finally {
    documentStreamScheduler?.cancel();
    stream.cancel();
  }

  if (!finalHandled && isActive()) {
    const fallbackAnswer = counselAskMode
      ? formatCounselStreamAnswer('', accumulated)
      : accumulated.trim()
        ? resolveDraftApplyAnswer('', accumulated)
        : '';
    if (streamIntoDocument && fallbackAnswer.trim()) {
      dispatchDocumentStreamFinal(workWorkspace.activeTabId, fallbackAnswer);
    }
    completeWithAnswer(fallbackAnswer, lastAiSources.drafting);
  }
}

async function streamDashboardResponse(params: {
  workspaceId: string;
  question: string;
  dashboardBrief: Record<string, unknown>;
  newHistory: LexCounselMessage[];
  ac: AbortController;
  guard: CounselSendGuard;
  onMessages: (updater: LexCounselMessage[] | ((prev: LexCounselMessage[]) => LexCounselMessage[])) => void;
  persist: (history: LexCounselMessage[]) => Promise<void>;
  onRegisterCancel?: (cancel: () => void) => void;
}): Promise<void> {
  const { workspaceId, question, dashboardBrief, newHistory, guard, onMessages, persist, onRegisterCancel } =
    params;
  const { isActive } = guard;
  const guardedOnMessages = guard.onMessages;
  const guardedPersist = guard.persist;
  const stream = createStreamingUpdater(guardedOnMessages, isActive);
  onRegisterCancel?.(() => stream.cancel());
  let accumulated = '';
  for await (const event of askDashboardQuestionStream(
    workspaceId,
    question,
    dashboardBrief,
    params.ac.signal,
  )) {
    if (!isActive()) return;
    if (event.type === 'node' && event.data?.name) {
      stream.immediate((prev) =>
        patchStreamingAiMessage(prev, {
          thinkingStatus: counselNodeStatusLabel(String(event.data.name), 'workspace'),
        }),
      );
    }
    if (event.type === 'token' && event.data?.text) {
      accumulated += event.data.text;
      stream.schedule((prev) => {
        const last = prev[prev.length - 1];
        return patchStreamingAiMessage(
          prev,
          counselTokenStreamPatch({
            accumulated,
            activityMode: 'workspace',
            streamIntoDocument: false,
            fallbackStatus: last?.thinkingStatus,
          }),
        );
      });
    }
    if (event.type === 'final' && event.data?.answerHtml != null) {
      if (!isActive()) return;
      stream.cancel();
      const aiMsg: LexCounselMessage = {
        role: 'ai',
        text: event.data.answerHtml as string,
        streaming: false,
      };
      const finalHistory = [...newHistory, aiMsg];
      guardedOnMessages(finalHistory);
      await guardedPersist(finalHistory);
    }
  }
}

async function streamCaseResponse(params: {
  workspace: LexCounselWorkspaceSnapshot;
  question: string;
  previousHistory: LexCounselMessage[];
  newHistory: LexCounselMessage[];
  wsCtx: ReturnType<typeof workspaceContextForSnapshot>;
  ac: AbortController;
  guard: CounselSendGuard;
  onMessages: (updater: LexCounselMessage[] | ((prev: LexCounselMessage[]) => LexCounselMessage[])) => void;
  persist: (history: LexCounselMessage[]) => Promise<void>;
  onRegisterCancel?: (cancel: () => void) => void;
}): Promise<void> {
  const {
    workspace,
    question,
    previousHistory,
    newHistory,
    wsCtx,
    guard,
    onMessages,
    persist,
    onRegisterCancel,
  } = params;
  const { isActive } = guard;
  const guardedOnMessages = guard.onMessages;
  const guardedPersist = guard.persist;
  const stream = createStreamingUpdater(guardedOnMessages, isActive);
  onRegisterCancel?.(() => stream.cancel());
  let accumulated = '';
  for await (const event of askCaseQuestionStream(
    workspace.caseData!,
    workspace.proceedings ?? [],
    question,
    previousHistory.map((m) => ({ role: m.role, text: m.text })),
    params.ac.signal,
    wsCtx,
  )) {
    if (!isActive()) return;
    if (event.type === 'node' && event.data?.name) {
      stream.immediate((prev) =>
        patchStreamingAiMessage(prev, {
          thinkingStatus: counselNodeStatusLabel(String(event.data.name), 'case'),
        }),
      );
    }
    if (event.type === 'authority' && event.data?.source) {
      stream.schedule((prev) => appendStreamingAuthority(prev, event.data.source as Source));
    }
    if (event.type === 'token' && event.data?.text) {
      accumulated += event.data.text;
      stream.schedule((prev) => {
        const last = prev[prev.length - 1];
        return patchStreamingAiMessage(
          prev,
          counselTokenStreamPatch({
            accumulated,
            activityMode: 'case',
            streamIntoDocument: false,
            fallbackStatus: last?.thinkingStatus,
          }),
        );
      });
    }
    if (event.type === 'final' && event.data?.answerHtml != null) {
      if (!isActive()) return;
      stream.cancel();
      const answer = event.data.answerHtml as string;
      const sources = (event.data.sources as Source[]) ?? lastAiSources.caseQa;
      const aiMsg: LexCounselMessage = {
        role: 'ai',
        text: answer,
        sources,
        streaming: false,
      };
      const finalHistory = [...newHistory, aiMsg];
      guardedOnMessages(finalHistory);
      await guardedPersist(finalHistory);
    }
  }
}

function workspaceContextForSnapshot(ws: LexCounselWorkspaceSnapshot) {
  const route = typeof window !== 'undefined' ? window.location.pathname : undefined;
  if (ws.screen === 'draft_editor') {
    return buildDraftEditorWorkspaceContext({
      workspaceId: ws.workspaceId,
      caseId: ws.caseId,
      draftId: ws.draftId,
      documentTitle: ws.draftTitle,
      activeTabId: ws.activeTabId,
      selectedText: ws.selectedText,
      hasSelection: Boolean(ws.selectedText?.trim()),
      route,
    });
  }
  if (ws.screen === 'case') {
    return buildCaseWorkspaceContext({
      workspaceId: ws.workspaceId,
      caseId: ws.caseId,
      route,
    });
  }
  if (ws.screen === 'library') {
    return buildLibraryWorkspaceContext({
      workspaceId: ws.workspaceId,
      route,
      researchSessionId: ws.researchSessionId ?? undefined,
    });
  }
  return buildDashboardWorkspaceContext({ workspaceId: ws.workspaceId, route });
}

interface ActiveCounselSend {
  sendId: string;
  userText: string;
  previousHistory: LexCounselMessage[];
  workspaceId: string;
  chatId: string | null;
  activeTabId?: string;
  onMessages: (updater: MessagesUpdater) => void;
  persist: (history: LexCounselMessage[]) => Promise<void>;
  cancelInFlight?: () => void;
}

export function useLexCounselChatActions() {
  const abortRef = useRef<AbortController | null>(null);
  const activeSendRef = useRef<ActiveCounselSend | null>(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const stopGeneration = useCallback(() => {
    const active = activeSendRef.current;
    if (!active) {
      abortRef.current?.abort();
      abortRef.current = null;
      return;
    }

    active.cancelInFlight?.();
    abortRef.current?.abort();
    abortRef.current = null;
    activeSendRef.current = null;

    flushSync(() => {
      active.onMessages(active.previousHistory);
      useLexCounselStore.getState().setInputDraft(active.userText);
      useLexCounselStore.getState().clearPendingChange();
    });

    dispatchDocumentStreamCancel(active.activeTabId);

    void active.persist(active.previousHistory).catch((err) => {
      console.warn('LexCounsel stop persist failed', err);
    });
  }, []);

  const sendMessage = useCallback(
    async (params: {
      text: string;
      slashCommand?: LexCounselSlashCommand | null;
      scope?: LexCounselSendScope;
      commandId?: string;
      workspace: LexCounselWorkspaceSnapshot;
      messages: LexCounselMessage[];
      workspaceId: string;
      userId: string;
      conversationId: string | null;
      onMessages: (updater: LexCounselMessage[] | ((prev: LexCounselMessage[]) => LexCounselMessage[])) => void;
      onConversationId: (id: string) => void;
      onDiagnostics: (d: Record<string, unknown> | null) => void;
    }) => {
      const {
        text,
        slashCommand,
        scope: sendScope,
        commandId,
        workspace,
        messages,
        workspaceId,
        userId,
        conversationId,
        onMessages,
        onConversationId,
        onDiagnostics,
      } = params;
      const displayText = text.trim();
      const parsed = slashCommand
        ? { command: slashCommand, args: displayText, raw: displayText }
        : parseSlashCommand(displayText);

      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      const previousHistory = [...messages];
      const userMsg: LexCounselMessage = {
        role: 'user',
        text: displayText,
        ...(parsed.command ? { command: parsed.command } : {}),
      };
      const newHistory = [...previousHistory, userMsg];
      let chatId = conversationId;
      const sendId = crypto.randomUUID();

      activeSendRef.current = {
        sendId,
        userText: displayText,
        previousHistory,
        workspaceId,
        chatId: conversationId,
        activeTabId: workspace.activeTabId,
        onMessages,
        persist: async (history: LexCounselMessage[]) => {
          try {
            if (!chatId) {
              chatId = await createConversation(workspaceId, {
                caseId: workspace.caseId ?? null,
                lawyerId: userId,
                title: displayText.substring(0, 48) + (displayText.length > 48 ? '…' : ''),
                history,
                isDefault: !conversationId,
              });
              onConversationId(chatId);
            } else {
              await updateConversation(workspaceId, chatId, history);
            }
          } catch (persistErr) {
            console.warn('LexCounsel conversation persist failed (chat continues)', persistErr);
          }
        },
      };

      onMessages([
        ...newHistory,
        {
          role: 'ai',
          text: '',
          streaming: true,
          sources: [],
          thinkingStatus: counselNodeStatusLabel(
            'gathering_context',
            counselActivityMode(
              useLexCounselStore.getState().editorCounselMode,
              workspace.screen,
            ),
          ),
        },
      ]);

      const persist = activeSendRef.current.persist;
      const guard = createCounselSendGuard(sendId, ac, activeSendRef, onMessages, persist);
      const registerStreamCancel = (cancel: () => void) =>
        registerCounselSendCancel(activeSendRef, sendId, cancel);

      const finishSend = () => {
        if (activeSendRef.current?.sendId === sendId) {
          activeSendRef.current = null;
        }
      };

      try {
        await persist(newHistory);
        if (activeSendRef.current?.sendId !== sendId) return;

        const wsCtx = workspaceContextForSnapshot(workspace);
        const question = parsed.command
          ? parsed.args || displayText.replace(/^\/\w+\s*/, '')
          : displayText;
        const storeState = useLexCounselStore.getState();
        const targetParagraphId =
          workspace.targetedParagraphId ??
          storeState.targetedParagraphId ??
          undefined;
        const targetParagraphHtml = workspace.targetParagraphHtml ?? undefined;

        if (workspace.screen === 'draft_editor') {
          if (parsed.command === 'improve' && !hasValidWorkspaceSelection(workspace)) {
            const aiMsg: LexCounselMessage = {
              role: 'ai',
              text: formatStructuredHtml('Improve selection', [
                {
                  heading: 'Select text first',
                  body: 'Highlight the passage in your draft, then ask again.',
                },
              ]),
              streaming: false,
            };
            const finalHistory = [...newHistory, aiMsg];
            guard.onMessages(finalHistory);
            await guard.persist(finalHistory);
            finishSend();
            return;
          }

          const refreshedWorkspace = useLexCounselStore.getState().workspace;
          let selectedMode = storeState.editorCounselMode;
          if (commandId || sendScope) {
            selectedMode = 'draft';
          }

          const userSwitchedBack = consumeUserSwitchedBackTelemetry(selectedMode);
          const modeResolution = resolveEditorCounselMode({
            selectedMode,
            question,
            hasSelection: hasValidWorkspaceSelection(refreshedWorkspace),
          });

          if (modeResolution.autoSwitched) {
            useLexCounselStore.getState().setEditorCounselMode(modeResolution.mode);
            recordAutoSwitchForTelemetry(
              modeResolution.modeBeforeSend,
              modeResolution.mode,
            );
          }

          trackEvent('lexcounsel_mode_classified', {
            signalMatched: modeResolution.classification.signalMatched,
            confidence: modeResolution.classification.confidence,
            modeBeforeSend: modeResolution.modeBeforeSend,
            modeAfterSend: modeResolution.mode,
            userSwitchedBackWithinNextMessage: userSwitchedBack,
          });

          const finalEditorMode = modeResolution.mode;

          window.dispatchEvent(
            new CustomEvent('lexcounsel-prepare-send', {
              detail: { text: question, editorCounselMode: finalEditorMode },
            }),
          );

          const postPrepareWorkspace = snapshotWorkspaceForDrafting(
            useLexCounselStore.getState().workspace,
          );
          const effectiveScope = inferDraftScope(postPrepareWorkspace, sendScope, question);
          const fallbackParagraphId =
            postPrepareWorkspace.targetedParagraphId ??
            useLexCounselStore.getState().targetedParagraphId ??
            targetParagraphId ??
            undefined;
          const fallbackParagraphHtml =
            postPrepareWorkspace.targetParagraphHtml ?? targetParagraphHtml;
          const resolvedTargetId =
            finalEditorMode !== 'draft' ||
            effectiveScope === 'document' ||
            effectiveScope === 'new_tab' ||
            effectiveScope === 'insert' ||
            effectiveScope === 'selection'
              ? undefined
              : fallbackParagraphId;
          const resolvedTargetHtml =
            finalEditorMode !== 'draft' ||
            effectiveScope === 'document' ||
            effectiveScope === 'new_tab' ||
            effectiveScope === 'insert' ||
            effectiveScope === 'selection'
              ? undefined
              : fallbackParagraphHtml;

          await streamDraftingResponse({
            workspace: postPrepareWorkspace,
            question,
            newHistory,
            wsCtx,
            ac,
            guard,
            editorCounselMode: finalEditorMode,
            modeSwitchNotice: modeResolution.modeSwitchNotice,
            draftScope: sendScope,
            commandId,
            targetParagraphId: resolvedTargetId ?? undefined,
            targetParagraphHtml: resolvedTargetHtml,
            fallbackParagraphId,
            fallbackParagraphHtml,
            onMessages,
            persist,
            onRegisterCancel: registerStreamCancel,
          });
          onDiagnostics(null);
          finishSend();
          return;
        }

        if (workspace.screen === 'case' && workspace.caseData) {
          await streamCaseResponse({
            workspace,
            question,
            previousHistory,
            newHistory,
            wsCtx,
            ac,
            guard,
            onMessages,
            persist,
            onRegisterCancel: registerStreamCancel,
          });
          onDiagnostics(null);
          finishSend();
          return;
        }

        if (workspace.screen === 'dashboard') {
          if (isDashboardBriefingQuestion(question)) {
            const result = answerDashboardQuestion(question, workspace.dashboardBrief);
            await revealTextGradually(result.text, newHistory, onMessages, persist, {
              structured: result.structured,
            });
            onDiagnostics(null);
            finishSend();
            return;
          }

          if (workspaceId) {
            await streamDashboardResponse({
              workspaceId,
              question,
              dashboardBrief: (workspace.dashboardBrief ?? {}) as Record<string, unknown>,
              newHistory,
              ac,
              guard,
              onMessages,
              persist,
              onRegisterCancel: registerStreamCancel,
            });
            onDiagnostics(null);
            finishSend();
            return;
          }
        }

        if (workspace.caseData) {
          await streamCaseResponse({
            workspace,
            question,
            previousHistory,
            newHistory,
            wsCtx,
            ac,
            guard,
            onMessages,
            persist,
            onRegisterCancel: registerStreamCancel,
          });
          onDiagnostics(null);
          finishSend();
          return;
        }

        if (workspaceId) {
          await streamDashboardResponse({
            workspaceId,
            question,
            dashboardBrief: {},
            newHistory,
            ac,
            guard,
            onMessages,
            persist,
            onRegisterCancel: registerStreamCancel,
          });
          onDiagnostics(null);
          finishSend();
          return;
        }

        const aiMsg: LexCounselMessage = {
          role: 'ai',
          text: formatStructuredHtml('Lex Counsel', [
            {
              heading: 'Get started',
              body: 'Open a case, draft, or stay on the dashboard to ask about your matters.',
            },
          ]),
          streaming: false,
        };
        const finalHistory = [...newHistory, aiMsg];
        onMessages(finalHistory);
        await persist(finalHistory);
        onDiagnostics(null);
        finishSend();
      } catch (err) {
        if (ac.signal.aborted) return;
        finishSend();
        const aiMsg: LexCounselMessage = {
          role: 'ai',
          text: formatLexCounselError(err),
          streaming: false,
        };
        onMessages([...newHistory, aiMsg]);
        console.error('LexCounsel send failed', err);
      }
    },
    [],
  );

  const loadConversationList = useCallback(
    async (workspaceId: string, userId: string, caseId: string | null) => {
      try {
        let chats = await loadConversations(workspaceId, userId, caseId);
        if (chats.length === 0 && caseId) {
          chats = await loadLegacyAiChats(workspaceId, userId, caseId);
        }
        return chats.map((c) => ({
          id: c.id,
          title: c.title,
          updatedAt: c.updatedAt,
          messageCount: c.history?.length ?? 0,
        }));
      } catch (e) {
        console.error('Failed to load conversations', e);
        return [];
      }
    },
    [],
  );

  return { sendMessage, cancel, stopGeneration, loadConversationList };
}
